/**
 * Detection Rules v2 — Elastic per-type hooks + types.
 *
 * Backing routes: cmd/admin-bff/rules_elastic.go.
 *
 * Per-rule paths use the `/r/` discriminator (Go 1.22 mux requirement).
 *
 * Query key convention:
 *   ["rules","elastic","list",filters]
 *   ["rules","elastic","detail",id]
 *   ["rules","elastic","corpora"]
 *   ["rules","elastic","imports"]
 *   ["rules","elastic","imports",id]
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./api";
import type { LintMessage, Rule, RuleSeverity, RuleTLP, RuleVersion } from "./rules";

// ---------- Enums ----------

export type ElasticRuleType =
    | "query"
    | "saved_query"
    | "threshold"
    | "eql"
    | "machine_learning"
    | "new_terms"
    | "threat_match"
    | "esql";

export type ElasticQueryLanguage = "kuery" | "lucene" | "eql" | "esql";

export const ELASTIC_RULE_TYPES: ReadonlyArray<{
    value: ElasticRuleType;
    label: string;
    description: string;
}> = [
    { value: "query", label: "Query", description: "KQL/Lucene over events" },
    { value: "saved_query", label: "Saved query", description: "Reference a saved Kibana query" },
    { value: "threshold", label: "Threshold", description: "Aggregation count over a field" },
    { value: "eql", label: "EQL", description: "Event Query Language sequences" },
    { value: "machine_learning", label: "Machine learning", description: "Anomaly score from an ML job" },
    { value: "new_terms", label: "New terms", description: "First-seen value of a field" },
    { value: "threat_match", label: "Threat match (indicator)", description: "Join events to threat-indicator index" },
    { value: "esql", label: "ES|QL", description: "Elastic SQL-like piped query" },
];

export const ELASTIC_QUERY_LANGUAGES: ReadonlyArray<{
    value: ElasticQueryLanguage;
    label: string;
}> = [
    { value: "kuery", label: "KQL (kuery)" },
    { value: "lucene", label: "Lucene" },
    { value: "eql", label: "EQL" },
    { value: "esql", label: "ES|QL" },
];

/**
 * Returns the language options compatible with a given rule type, per
 * Elastic's documented combinations.
 */
export function compatibleLanguages(rt: ElasticRuleType): ElasticQueryLanguage[] {
    switch (rt) {
        case "eql":
            return ["eql"];
        case "esql":
            return ["esql"];
        case "machine_learning":
            return [];
        default:
            return ["kuery", "lucene"];
    }
}

// ---------- Types ----------

export interface ElasticThresholdCardinality {
    field: string;
    value: number;
}
export interface ElasticThresholdSpec {
    field?: string | string[];
    value: number;
    cardinality?: ElasticThresholdCardinality[];
}

export interface ElasticThreatTactic {
    id: string; // TA####
    name: string;
    reference?: string;
}
export interface ElasticThreatTechnique {
    id: string; // T#### or T####.###
    name: string;
    reference?: string;
    subtechnique?: ElasticThreatTechnique[];
}
export interface ElasticThreatBlock {
    framework: string;
    tactic: ElasticThreatTactic;
    technique?: ElasticThreatTechnique[];
}

export interface ElasticRequiredField {
    name: string;
    type: string;
    ecs?: boolean;
}

export interface ElasticRelatedIntegration {
    package: string;
    version: string;
}

export interface ElasticThreatMappingEntry {
    field: string;
    type: string;
    value: string;
}
export interface ElasticThreatMapping {
    entries: ElasticThreatMappingEntry[];
}

export interface ElasticMeta {
    ruleType: ElasticRuleType;
    queryLanguage: ElasticQueryLanguage | "";
    query: string;
    indexPatterns: string[];
    riskScore: number;
    riskScoreMapping: unknown[];
    severityMapping: unknown[];
    threat: ElasticThreatBlock[];
    falsePositives: string[];
    references: string[];
    investigationFields: string[];
    requiredFields: ElasticRequiredField[];
    setup: string;
    relatedIntegrations: ElasticRelatedIntegration[];
    note: string;
    interval: string;
    from: string;
    buildingBlockType?: string;
    threshold?: ElasticThresholdSpec;
    eventCategoryOverride?: string;
    machineLearningJobId?: string;
    anomalyThreshold?: number;
    newTermsFields?: string[];
    historyWindowStart?: string;
    threatIndex?: string[];
    threatQuery?: string;
    threatMapping?: ElasticThreatMapping[];
    threatFilters?: unknown[];
    threatIndicatorPath?: string;
}

export interface ElasticListRow {
    id: string;
    slug: string;
    name: string;
    severity: string;
    status: string;
    currentVersion: number;
    publishedVersion?: number;
    tags: string[];
    imported: boolean;
    license?: string;
    updatedAt: string;
    ruleType: ElasticRuleType;
    queryLanguage: ElasticQueryLanguage | "";
    indexPatterns: string[];
    riskScore: number;
}

export interface ElasticListFilters {
    q?: string;
    ruleType?: ElasticRuleType | "";
    queryLanguage?: ElasticQueryLanguage | "";
    indexPattern?: string;
    status?: string;
    imported?: boolean;
    limit?: number;
    offset?: number;
}

export interface ElasticDetail {
    rule: Rule;
    elasticMeta: ElasticMeta;
    currentVersion: RuleVersion;
    recentDryRuns: Array<unknown>;
}

export interface ElasticCorpus {
    slug: string;
    name: string;
    kind: string;
    description: string;
    itemCount: number;
    objectUrl: string;
}

export interface ElasticImportRow {
    id: string;
    registrySlug: string;
    temporalWorkflowId?: string;
    temporalRunId?: string;
    status: "running" | "ok" | "error" | "cancelled" | "partial";
    counters: Record<string, unknown>;
    startedAt: string;
    finishedAt?: string;
    elapsedMs: number;
}

export interface ElasticDryRunResult {
    engine: "stubbed" | string;
    note?: string;
    status: "ok" | string;
    matches: number;
}

// ---------- Queries ----------

function toQuery(filters: ElasticListFilters): string {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(filters)) {
        if (v === undefined || v === null || v === "") continue;
        p.set(k, String(v));
    }
    return p.toString();
}

export function useElasticList(filters: ElasticListFilters = {}) {
    return useQuery({
        queryKey: ["rules", "elastic", "list", filters] as const,
        queryFn: () =>
            api.get<{ rules: ElasticListRow[]; total: number }>(
                `/api/v2/rules/elastic?${toQuery(filters)}`,
            ),
    });
}

export function useElasticRules(filters: ElasticListFilters = {}) {
    // Alias for parity with useSigmaList — landing card uses .total
    return useElasticList(filters);
}

export function useElasticDetail(id: string | undefined) {
    return useQuery({
        queryKey: ["rules", "elastic", "detail", id] as const,
        queryFn: () => api.get<ElasticDetail>(`/api/v2/rules/elastic/r/${id}`),
        enabled: !!id,
    });
}

export function useElasticCorpora() {
    return useQuery({
        queryKey: ["rules", "elastic", "corpora"] as const,
        queryFn: () =>
            api
                .get<{ corpora: ElasticCorpus[] }>(`/api/v2/rules/elastic/corpora`)
                .then((d) => d.corpora ?? []),
        staleTime: 60_000,
    });
}

export function useElasticImports() {
    return useQuery({
        queryKey: ["rules", "elastic", "imports"] as const,
        queryFn: () =>
            api
                .get<{ imports: ElasticImportRow[] }>(`/api/v2/rules/elastic/imports`)
                .then((d) => d.imports ?? []),
        refetchInterval: 5_000,
    });
}

export function useElasticImport(id: string | undefined) {
    return useQuery({
        queryKey: ["rules", "elastic", "imports", id] as const,
        queryFn: () =>
            api
                .get<{ import: ElasticImportRow }>(`/api/v2/rules/elastic/imports/${id}`)
                .then((d) => d.import),
        enabled: !!id,
        refetchInterval: 3_000,
    });
}

// ---------- Mutations ----------

export interface CreateElasticInput {
    slug?: string;
    name: string;
    description: string;
    severity: RuleSeverity;
    tlp: RuleTLP;
    tags: string[];
    content: string;
    meta?: {
        ruleType?: ElasticRuleType;
        queryLanguage?: ElasticQueryLanguage;
        riskScore?: number;
        indexPatterns?: string[];
        setup?: string;
        interval?: string;
        from?: string;
    };
}

export function useCreateElastic() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: CreateElasticInput) =>
            api.post<{ rule: Rule; elasticMeta: ElasticMeta; lintMessages: LintMessage[] }>(
                `/api/v2/rules/elastic`,
                input,
            ),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["rules", "elastic", "list"] });
        },
    });
}

export type PatchElasticMetaInput = Partial<ElasticMeta>;

export function usePatchElasticMeta(id: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (patch: PatchElasticMetaInput) =>
            api.patch<{ elasticMeta: ElasticMeta }>(`/api/v2/rules/elastic/r/${id}`, patch),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["rules", "elastic", "detail", id] });
            void qc.invalidateQueries({ queryKey: ["rules", "elastic", "list"] });
        },
    });
}

export function usePutElasticContent(id: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: { content: string; note?: string }) =>
            api.put<{ version: RuleVersion; lintMessages: LintMessage[] }>(
                `/api/v2/rules/elastic/r/${id}/content`,
                input,
            ),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["rules", "elastic", "detail", id] });
            void qc.invalidateQueries({ queryKey: ["rules", "elastic", "list"] });
        },
    });
}

export function useLintElastic(id: string | undefined) {
    return useMutation({
        mutationFn: (input: { content: string }) =>
            api.post<{ lintMessages: LintMessage[] }>(
                `/api/v2/rules/elastic/r/${id}/lint`,
                input,
            ),
    });
}

export function useDryRunElastic(id: string) {
    return useMutation({
        mutationFn: () =>
            api.post<{ result: ElasticDryRunResult }>(`/api/v2/rules/elastic/r/${id}/dry-run`, {}),
    });
}

export function useStartElasticImport() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: { registrySlug: string }) =>
            api.post<{ importId: string; temporalWorkflowId: string; status: string; startedAt: string }>(
                `/api/v2/rules/elastic/imports`,
                input,
            ),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["rules", "elastic", "imports"] });
        },
    });
}

// ---------- Constants ----------

export const ELASTIC_REGISTRIES: ReadonlyArray<{
    slug: string;
    name: string;
    license: string;
    description: string;
    expectedRules: number;
    expectedDurationMin: number;
}> = [
    {
        slug: "elastic/detection-rules",
        name: "elastic / detection-rules",
        license: "Elastic-2.0",
        description:
            "Elastic Security's official detection-rules repository (~600 rules covering all 8 rule types).",
        expectedRules: 600,
        expectedDurationMin: 3,
    },
];

/** Risk-score color band — maps to the existing severity tokens. */
export function riskScoreBand(score: number): "muted" | "warn" | "danger" {
    if (score >= 70) return "danger";
    if (score >= 40) return "warn";
    return "muted";
}
